/**
 * Class Service - API client for server-side class management
 *
 * Classes are stored on the server with stable IDs that persist across devices.
 * Student names remain local (in SQLite) for privacy.
 */

import { api } from './api-client';

// ============================================================================
// Types
// ============================================================================

export interface ServerClass {
  classId: string;
  userId: string;
  name: string;
  grade: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateClassInput {
  name: string;
  grade?: string;
}

export interface UpdateClassInput {
  name?: string;
  grade?: string;
}

export interface ClassListResponse {
  items: ServerClass[];
  total: number;
}

export interface CreateClassResponse {
  class_id: string;
  name: string;
  grade: string | null;
  message: string;
}

// ============================================================================
// API Response Types (snake_case from server)
// ============================================================================

interface ServerClassResponse {
  class_id: string;
  user_id: string;
  name: string;
  grade: string | null;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Transformers
// ============================================================================

function transformClass(data: ServerClassResponse): ServerClass {
  return {
    classId: data.class_id,
    userId: data.user_id,
    name: data.name,
    grade: data.grade,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

// ============================================================================
// Service Class
// ============================================================================

class ClassService {
  /**
   * Create a new class on the server.
   * Returns the server-generated classId.
   */
  async createClass(
    input: CreateClassInput,
    signal?: AbortSignal
  ): Promise<ServerClass> {
    const response = await api.post<CreateClassResponse>(
      '/classes',
      input,
      { signal }
    );

    // Return a ServerClass object with the new ID
    return {
      classId: response.class_id,
      userId: '', // Will be filled by server
      name: response.name,
      grade: response.grade,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  /**
   * List all classes for the authenticated user.
   */
  async getClasses(
    includeDeleted = false,
    signal?: AbortSignal
  ): Promise<ServerClass[]> {
    const params = new URLSearchParams();
    if (includeDeleted) {
      params.set('include_deleted', 'true');
    }

    const response = await api.get<{ items: ServerClassResponse[]; total: number }>(
      `/classes?${params.toString()}`,
      { signal }
    );

    return response.items.map(transformClass);
  }

  /**
   * Get a single class by ID.
   */
  async getClass(classId: string, signal?: AbortSignal): Promise<ServerClass> {
    const response = await api.get<ServerClassResponse>(
      `/classes/${classId}`,
      { signal }
    );

    return transformClass(response);
  }

  /**
   * Update a class name and/or grade.
   */
  async updateClass(
    classId: string,
    input: UpdateClassInput,
    signal?: AbortSignal
  ): Promise<ServerClass> {
    const response = await api.put<ServerClassResponse>(
      `/classes/${classId}`,
      input,
      { signal }
    );

    return transformClass(response);
  }

  /**
   * Soft delete a class.
   * Scans associated with this class are NOT deleted.
   */
  async deleteClass(classId: string, signal?: AbortSignal): Promise<void> {
    await api.delete(`/classes/${classId}`, { signal });
  }
}

// Export singleton instance
export const classService = new ClassService();
